import FingerprintJS from '@fingerprintjs/fingerprintjs'
import CryptoJS from 'crypto-js'

import {OsType} from '~/types/bussiness/callApp'

import {
  DOKIMO_MEMBER_INFO_KEY,
  DOKIMO_TOKEN_KEY,
  LOCAL_STORAGE_KEY_DID,
} from './constant'
import {getStorageItem, setStorageItem} from './storage'

/**
 * 获取浏览器指纹
 * @returns 浏览器指纹
 */
export async function getBrowserFingerprint() {
  const cachedFingerprint = getStorageItem(LOCAL_STORAGE_KEY_DID) || ''
  // 如果已经生成过指纹，直接返回
  if (cachedFingerprint) {
    return cachedFingerprint
  }

  // 初始化 FingerprintJS
  const fp = await FingerprintJS.load()
  // 获取指纹
  const result = await fp.get()
  // 缓存指纹
  setStorageItem(LOCAL_STORAGE_KEY_DID, result.visitorId)

  return result.visitorId
}

/**
 * 生成安全的 nonce
 * 优先级：Web Crypto API > crypto-js > 后备方案
 */
export const generateSecureNonce = async (): Promise<string> => {
  // 方案 1：使用 Web Crypto API（最安全）
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    try {
      const randomBytes = new Uint8Array(16)
      crypto.getRandomValues(randomBytes)

      // 转换为十六进制
      const hexString = Array.from(randomBytes)
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('')

      return hexString
    } catch (error) {
      console.warn('Web Crypto API 生成 nonce 失败，使用后备方案', error)
    }
  }

  // 方案 2：使用 crypto-js（兼容性好）
  try {
    const randomString = [
      Math.random().toString(36),
      Date.now().toString(36),
      Math.random().toString(36),
      typeof performance !== 'undefined'
        ? performance.now().toString(36)
        : Math.random().toString(36),
    ].join('')

    const hashedNonce = CryptoJS.SHA256(randomString).toString(CryptoJS.enc.Hex)

    return hashedNonce.substring(0, 32)
  } catch (error) {
    console.error('生成 nonce 失败', error)
    // 最终后备方案
    return `${Date.now()}-${Math.random().toString(36)}`
  }
}

/**
 * 获取当前环境
 * @returns 'development' | 'production' | 'test'
 */
export const getEnvironment = (): 'development' | 'production' | 'test' => {
  console.log('ENV========>', ENV)
  return ENV as 'development' | 'production' | 'test'
}

/**
 * 判断是否是开发环境
 * @returns boolean
 */
export const isDevelopment = (): boolean => {
  return ENV === 'development'
}

/**
 * 判断是否是生产环境
 * @returns boolean
 */
export const isProduction = (): boolean => {
  return ENV === 'production'
}

/**
 * 使代码暂停指定的毫秒数
 * @param ms - 暂停的毫秒数
 * @returns Promise
 */
export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

/**
 * 在下一个空闲时间执行回调函数
 * @param fn - 要执行的回调函数
 */
export const nextTick = (fn: IdleRequestCallback) => {
  const callback =
    typeof window !== 'undefined' &&
    typeof window.requestIdleCallback === 'function'
      ? window.requestIdleCallback
      : (e: IdleRequestCallback) => {
          const n = Date.now()

          return setTimeout(() => {
            e({
              didTimeout: !1,
              timeRemaining() {
                return Math.max(0, 50 - (Date.now() - n))
              },
            })
          }, 1)
        }

  callback(fn, {timeout: 300})
}

/**
 * 检测移动端操作系统
 * @returns 'iOS' | 'Android' | 'Other'
 */
export const getMobileOS = (): OsType => {
  const {userAgent} = navigator
  // 检查iOS
  if (/iPad|iPhone|iPod/.test(userAgent)) {
    return 'ios'
  }

  // 检查Android
  if (/android/i.test(userAgent)) {
    return 'android'
  }

  // 其他环境
  return 'other'
}

/**
 * 判断是否在浏览器环境中运行
 * @returns boolean
 */
export function isBrowser() {
  return !!(
    typeof window !== 'undefined' &&
    window.document &&
    window.document.createElement
  )
}

/**
 * 分割数字
 * @param num 数字
 * @returns 分割后的数字
 */
export function splitPriceNumber(num: number) {
  // 将数字转换为字符串
  const numStr = num.toString()
  // 计算分割点
  const splitIndex = numStr.length - 2 // 取倒数第二位作为分割点

  // 分割字符串
  const part1 = numStr.slice(0, splitIndex) || '0'
  const part2 = numStr.slice(splitIndex)

  // 返回结果
  return [part1, part2]
}

export type DokimoMemberInfo = {
  id?: number
  oid?: number
  avatar?: string
  avatar_url?: string
  avatar_urls?: {
    aspect_low?: {
      urls?: string[]
    }
    origin?: {
      urls?: string[]
    }
  }
  name?: string
  nickname?: string
  username?: string
  [key: string]: any
}

export type DokimoAuth = {
  token: string
  memberInfo?: DokimoMemberInfo
}

const getStoredMemberInfo = (): DokimoMemberInfo | undefined => {
  if (!isBrowser()) {
    return undefined
  }

  const storedMemberInfo = window.localStorage.getItem(DOKIMO_MEMBER_INFO_KEY)
  if (!storedMemberInfo) {
    return undefined
  }

  try {
    return JSON.parse(storedMemberInfo) as DokimoMemberInfo
  } catch (error) {
    window.localStorage.removeItem(DOKIMO_MEMBER_INFO_KEY)
    return undefined
  }
}

export const getStoredDokimoAuth = (): DokimoAuth | null => {
  if (!isBrowser()) {
    return null
  }

  const token = window.localStorage.getItem(DOKIMO_TOKEN_KEY)
  if (!token) {
    return null
  }

  return {
    token,
    memberInfo: getStoredMemberInfo(),
  }
}

export const setStoredDokimoAuth = (auth: DokimoAuth) => {
  if (!isBrowser()) {
    return
  }

  window.localStorage.setItem(DOKIMO_TOKEN_KEY, auth.token)
  if (auth.memberInfo) {
    window.localStorage.setItem(
      DOKIMO_MEMBER_INFO_KEY,
      JSON.stringify(auth.memberInfo)
    )
  }
}

export const clearStoredDokimoAuth = () => {
  if (!isBrowser()) {
    return
  }

  window.localStorage.removeItem(DOKIMO_TOKEN_KEY)
  window.localStorage.removeItem(DOKIMO_MEMBER_INFO_KEY)
}

export const validateArchiveFile = (
  file: File,
  maxSize: number = 512 * 1024 * 1024
) => {
  if (file.size > maxSize) {
    return 'invalidSize'
  }

  return null
}

export const readFileAsBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => {
      const result = String(reader.result || '')
      resolve(result.split(',')[1] || '')
    }

    reader.onerror = () => {
      reject(reader.error || new Error('read file failed'))
    }

    reader.readAsDataURL(file)
  })
